import { useTranslation } from "react-i18next";
import React, { useCallback, useState, useEffect } from 'react';
import { useAsync } from 'react-async';
import { Button } from 'reactstrap';
import { CSSTransition } from 'react-transition-group';
import HideCookieConsent from 'Components/General/HideCookieconsent';
import logo from 'assets/logo_green_tm.png';
import Header from '../Components/Header/Header';
import 'css/staticpage.scss';

const loadInviteData = async ({
  inviteId
}) => {
  try {
    console.log('Loading invitation data for:', inviteId); // Try to fetch invitation data from API
    // Use absolute URL to ensure we hit the API server, not the webapp server

    const apiUrl = process.env.REACT_APP_API_URL || 'https://api.heresmyinfo.com'; // Remove trailing /api if it exists to avoid duplicate /api/api/

    const baseUrl = apiUrl.endsWith('/api') ? apiUrl.slice(0, -4) : apiUrl; // Record the click for deferred deeplink matching (fire and forget)

    fetch(`${baseUrl}/api/invitations/record-click`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        inviteCode: inviteId
      })
    }).catch(e => console.error('Failed to record click:', e));
    const response = await fetch(`${baseUrl}/api/invitations/validate/${inviteId}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json'
      }
    });
    const data = await response.json();
    console.log('Invitation data loaded:', data);

    if (response.ok) {
      return data;
    } else {
      console.error('Failed to load invitation:', response.status, response.statusText, data); // Return the API error response so the UI can display the specific error message

      return data;
    }
  } catch (error) {
    console.error('Error loading invitation:', error); // Return a fallback object so the page still renders

    return {
      success: false,
      error: error.message,
      invitation: null
    };
  }
};

export default (({
  match
}) => {
  const { t } = useTranslation();
  console.log('Invite page loaded for:', match.params.inviteId);
  const inviteId = match.params.inviteId;
  const {
    data,
    error,
    isLoading
  } = useAsync(loadInviteData, {
    inviteId
  });
  const [showContent, setShowContent] = useState(false);
  const [copied, setCopied] = useState(false);
  const openInApp = useCallback(() => {
    // Determine OS
    const userAgent = navigator.userAgent || navigator.vendor || window.opera;
    const isAndroid = /android/i.test(userAgent);
    const isIOS = /iPad|iPhone|iPod/.test(userAgent) && !window.MSStream; // Custom Scheme URL
    // We can't import inviteAppScheme dynamically easily without updating the import above, 
    // but since we are replacing the whole file's logic for this function, we will import it properly or define it.
    // Ideally we update the imports. Let's assume we update the import to include inviteAppScheme.
    // Since I cannot change the import line in this block easily, I will use the known scheme structure.

    const appUrl = `heresmyinfo://invite/${inviteId}`; // Store URLs

    const IOS_STORE = 'https://apps.apple.com/us/app/heresmyinfo/id1439939855';
    const ANDROID_STORE = 'https://play.google.com/store/apps/details?id=com.heresmyinfo.client'; // Fallback logic

    const fallbackToStore = () => {
      if (isAndroid) {
        window.location.href = ANDROID_STORE;
      } else if (isIOS) {
        window.location.href = IOS_STORE;
      } else {
        // Desktop - stay here or show a "Download" badge
        console.log("Desktop user - staying on page");
      }
    };

    if (isAndroid || isIOS) {
      // 1. Try to open the app
      window.location.href = appUrl; // 2. Set a timeout to redirect to store if app doesn't open
      // If the app opens, the browser goes to background and the timeout might be cleared or delayed significantly.
      // If the app doesn't open, the user stays on the page and the timeout fires.

      setTimeout(() => {
        // We check if the user is still active on the window, though this is tricky.
        // Simple approach: just redirect.
        // Better approach: check document.hidden but timing is race-condition prone.
        // For "Intermediary" pages, we usually show a "Download App" button explicitly too.
        // Let's just prompt the user or redirect. 
        // A direct redirect can be annoying if they cancelled the "Open in App" dialog.
        // But for a "get the app" flow, it's safer.
        fallbackToStore();
      }, 2500);
    } else {
      alert("This feature is designed for mobile. Please open this link on your phone!");
    }
  }, [inviteId]); // Show content after loading completes

  useEffect(() => {
    if (!isLoading) {
      setShowContent(true);
    }
  }, [isLoading]);
  return <>
      <Header />
      <HideCookieConsent />
      <CSSTransition in={isLoading} timeout={300} classNames='brand-background' unmountOnExit appear onExited={() => setShowContent(true)}>
        <div className='full-screen brand-background '>
          <p className='primary-text'>{t("Loading Invite details...")}</p>
        </div>
      </CSSTransition>

      {showContent && <div className='full-screen'>
          <div className='d-flex flex-column align-items-center'>
            <img src={logo} alt={t("HeresMyInfo")} width='250' />

            {error ? <div className='p-3 pt-4 text-center'>
                <h4 className='text-danger'>{t("Oops! Page not found")}</h4>
                <p className='text-secondary'>{t("This invitation link is invalid or has expired.")}</p>
                <p className='text-muted'>{t("Error:")}{error.message || 'Unknown error'}
                </p>
              </div> : data && data.success && data.data && data.data.invitation ? <div className='p-3 pt-4 text-center'>
                <h4 className='text-success'>{t("Invitation Found!")}</h4>
                <p className='text-secondary'>
                  {data.data.invitation.fromEmail || data.data.invitation.fromUsername}{t("has invited you to connect.")}</p>
                <p className='text-muted'>{t("Install HeresMyInfo to see everything this user is sharing with you.")}</p>
              </div> : <div className='p-3 pt-4 text-center'>
                <h4 className='text-warning'>{t("Invitation Not Found")}</h4>
                <p className='text-secondary'>{t("This invitation link is invalid or has expired.")}</p>
                <p className='text-muted'>{t("Install HeresMyInfo to connect with others.")}</p>
              </div>}

            <div className='d-flex flex-column align-items-center mt-4'>
              <Button color='primary' size='lg' className='mb-3' onClick={openInApp} style={{
            minWidth: '280px'
          }}>{t("Open in Mobile App")}</Button>

              <div className='text-center mt-2'>
                <p className='text-muted small mb-2'>{t("If the app doesn't open automatically, copy the code below:")}</p>
                <Button outline color='secondary' size='sm' onClick={() => {
              navigator.clipboard.writeText(inviteId);
              setCopied(true);
              setTimeout(() => setCopied(false), 2000);
            }}>
                  {copied ? '✅ Code Copied!' : `Copy Code: ${inviteId}`}
                </Button>
              </div>
            </div>
          </div>
        </div>}
    </>;
}); // Cache bust